import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  subscribeToUserData, 
  initializeUserDocument,
  completeActiveQuest,
  abandonActiveQuest,
  addNewQuest,
  subscribeToUserQuestRequests
} from '../services/firestoreService';
import { subscribeToAvailableQuest } from '../services/adminService';
import {
  calculateTotalXP,
  getCompletedQuests,
  getFailedQuests,
  getActiveQuest,
  getQuestHistory,
  calculateQuestStats,
  getXPInPeriod,
  hasCompletedQuestToday,
  getQuestStreak
} from '../utils/xpCalculations';

export const useUserData = () => {
  const { currentUser } = useAuth();
  const [userData, setUserData] = useState(null);
  const [questRequests, setQuestRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hydratedActiveQuest, setHydratedActiveQuest] = useState(null);
  const [error, setError] = useState(null);

  // Derived data from userData
  const xpHistory = userData?.xpHistory || {};
  const username = userData?.username || 'Anonymous User';
  
  // XP calculations
  const totalXP = calculateTotalXP(xpHistory);
  const questStats = calculateQuestStats(xpHistory, userData); // Stats still rely on presence of ref
  const activeQuestRef = getActiveQuest(userData); // Only contains { id, createdAt, updatedAt }
  const questHistory = getQuestHistory(xpHistory);
  const completedQuests = getCompletedQuests(xpHistory);
  const failedQuests = getFailedQuests(xpHistory);
  
  // Additional statistics
  const weeklyXP = getXPInPeriod(xpHistory, 7);
  const monthlyXP = getXPInPeriod(xpHistory, 30);
  const completedToday = hasCompletedQuestToday(xpHistory);
  const streakInfo = getQuestStreak(xpHistory);

  // Initialize user document and subscribe to changes
  useEffect(() => {
    if (!currentUser) {
      setUserData(null);
      setLoading(false);
      return;
    }

    const initializeAndSubscribe = async () => {
      try {
        setLoading(true);
        
        // First, ensure user document exists
        await initializeUserDocument(
          currentUser.uid, 
          currentUser.displayName || currentUser.email?.split('@')[0] || 'Anonymous User'
        );
        
        // Then start subscription
        const unsubscribe = subscribeToUserData(currentUser.uid, (data) => {
          if (data === null) {
            setError('Failed to load user data');
          } else {
            setUserData(data);
            setError(null);
          }
          setLoading(false);
        });

        return unsubscribe;
      } catch (err) {
        console.error('Error initializing user:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    let unsubscribe;
    initializeAndSubscribe().then(unsub => {
      unsubscribe = unsub;
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [currentUser]);

  // Hydrate active quest details from availableQuests using the stored reference id
  useEffect(() => {
    // Reset hydration on user switch or when no active quest
    if (!activeQuestRef?.id) {
      setHydratedActiveQuest(null);
      return;
    }

    const unsubscribe = subscribeToAvailableQuest(activeQuestRef.id, (quest) => {
      if (quest) {
        // Merge stored timestamps into the hydrated quest
        setHydratedActiveQuest({
          ...quest,
          id: quest.id,
          createdAt: activeQuestRef.createdAt || quest.createdAt,
          updatedAt: activeQuestRef.updatedAt || quest.updatedAt,
          status: 'active',
          type: quest.type || 'quest'
        });
      } else {
        setHydratedActiveQuest(null);
      }
    });

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [activeQuestRef?.id, activeQuestRef?.createdAt, activeQuestRef?.updatedAt]);

  // Subscribe to user quest requests
  useEffect(() => {
    if (!currentUser) {
      setQuestRequests([]);
      return;
    }

    const unsubscribe = subscribeToUserQuestRequests(currentUser.uid, (requests) => {
      setQuestRequests(requests);
    });

    return () => {
      unsubscribe();
    };
  }, [currentUser]);

  // Quest management functions
  const startNewQuest = useCallback(async (questData) => {
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      const questId = await addNewQuest(currentUser.uid, {
        ...questData,
        status: 'active',
        type: 'quest'
      });
      return questId;
    } catch (err) {
      console.error('Error starting new quest:', err);
      throw err;
    }
  }, [currentUser]);

  const completeQuest = useCallback(async (questId, xpValue) => {
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      await completeActiveQuest(currentUser.uid, xpValue);
    } catch (err) {
      console.error('Error completing quest:', err);
      throw err;
    }
  }, [currentUser]);

  const abandonQuest = useCallback(async (questId) => {
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      await abandonActiveQuest(currentUser.uid);
    } catch (err) {
      console.error('Error abandoning quest:', err);
      throw err;
    }
  }, [currentUser]);

  // Helper to get formatted stats for components
  const getFormattedStats = useCallback(() => {
    return {
      ...questStats,
      totalXP,
      weeklyXP,
      monthlyXP,
      completedToday,
      currentStreak: streakInfo.currentStreak,
      longestStreak: streakInfo.longestStreak,
      username
    };
  }, [questStats, totalXP, weeklyXP, monthlyXP, completedToday, streakInfo, username]);

  // Helper to check if user has active quest
  const hasActiveQuest = useCallback(() => {
    return !!hydratedActiveQuest;
  }, [hydratedActiveQuest]);

  // Helper to get active quest with additional formatting
  const getFormattedActiveQuest = useCallback(() => {
    if (!hydratedActiveQuest) return null;

    return {
      ...hydratedActiveQuest,
      title: hydratedActiveQuest.title || `Quest ${hydratedActiveQuest.id.slice(-6)}`,
      reward: hydratedActiveQuest.value,
      timeStarted: hydratedActiveQuest.createdAt ? new Date(hydratedActiveQuest.createdAt).toLocaleString() : 'Unknown',
      estimatedTimeLeft: '30 min'
    };
  }, [hydratedActiveQuest]);

  // Helper to get formatted quest history — hydrate titles from quest requests
  const getFormattedQuestHistory = useCallback(() => {
    // Build maps to resolve quest titles from multiple sources
    const titleMap = {};
    // Source 1: quest requests (questId → questData.title)
    questRequests.forEach(req => {
      if (req.questId && req.questData?.title) {
        titleMap[req.questId] = req.questData.title;
      }
      // Also map by the quest request's own ID (in case xpHistory uses request IDs)
      if (req.id && req.questData?.title) {
        titleMap[req.id] = req.questData.title;
      }
    });

    return questHistory.map(quest => {
      // Try to resolve title: stored in xpHistory → from quest requests → fallback
      const resolvedTitle = quest.title || titleMap[quest.id] || null;
      return {
        ...quest,
        title: resolvedTitle,
        reward: quest.value,
        difficulty: quest.difficulty || 'medium'
      };
    });
  }, [questHistory, questRequests]);

  // Helper to get pending quest requests
  const getPendingQuestRequests = useCallback(() => {
    return questRequests.filter(request => request.status === 'pending');
  }, [questRequests]);

  // Helper to get approved quest requests
  const getApprovedQuestRequests = useCallback(() => {
    return questRequests.filter(request => request.status === 'approved');
  }, [questRequests]);

  // Helper to get rejected quest requests
  const getRejectedQuestRequests = useCallback(() => {
    return questRequests.filter(request => request.status === 'rejected');
  }, [questRequests]);

  return {
    // Raw data
    userData,
    xpHistory,
    username,
    questRequests,
    
    // Loading states
    loading,
    error,
    
    // Processed data
    totalXP,
    activeQuest: hydratedActiveQuest,
    questHistory,
    completedQuests,
    failedQuests,
    
    // Statistics
    questStats,
    weeklyXP,
    monthlyXP,
    completedToday,
    streakInfo,
    
    // Quest management functions
    startNewQuest,
    completeQuest,
    abandonQuest,
    
    // Helper functions
    getFormattedStats,
    hasActiveQuest,
    getFormattedActiveQuest,
    getFormattedQuestHistory,
    getPendingQuestRequests,
    getApprovedQuestRequests,
    getRejectedQuestRequests,
    
    // Utility
    isAuthenticated: !!currentUser
  };
}; 